import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../../services/api";
import { ShowGenericErrorToast, ShowToast } from "../../../services/toast";
import { CallProcessingLoadingState } from "../../praxis-call/components/CallProcessingLoadingState";
import { useUser } from "../../../services/user-context";
import { PRAXIS_PLAY_EVENTS } from "../../../integrations/posthog/events";
import { useAnalytics } from "../../../integrations/posthog/useAnalytics";
import { withBackoff } from "../../../services/with-backoff";
import {
  executeEndReasonDisconnect,
  clearCallStateOnDisconnectError,
} from "../../../contexts/disconnect-helpers";
import { getLoggerRef } from "../../../integrations/logger/logger-bridge";

export const PraxisCallContext = React.createContext({});

export function PraxisCallProvider({ children }) {
  const { captureEvent } = useAnalytics();
  const [callDetails, setCallDetails] = React.useState<{
    id?: string;
    clientSecret?: string;
    isVideoRecording?: boolean;
    certificationAssignment?: string;
    beginTime?: string | number;
    brandAndPI?: string;
    practitionerType?: string;
    voice?: string;
    objectionLevel?: string;
    personalityType?: string;
    seedContext?: any;
  }>({});
  const [dataSavingProgress, setDataSavingProgress] = React.useState(0);
  const { userInfo } = useUser() as any;
  const [assignment, setAssignment] = React.useState<any>(null);
  const [loading, setLoading] = React.useState(false);
  const [callStatus, setCallStatus] = React.useState("idle"); // idle, calling, connected, ended
  const [report, setReport] = React.useState<any>({});
  const [fetchingReports, setFetchingReports] = React.useState(false);
  const transcriptRef = useRef<{ role: string; content: string }[]>([]);
  const sentenceCacheRef = useRef<string[]>([]);
  const abortRef = useRef(false);

  useEffect(() => {
    if (callDetails?.id) abortRef.current = true;
  }, [callDetails?.id]);

  const [showFeedback, setShowFeedback] = React.useState(false);
  const [detailedReport, setDetailedReport] = React.useState<any>(null);
  const [detailedReportFetching, setDetailedReportFetching] = React.useState(false);

  const fetchDetailedReport = async () => {
    setDetailedReportFetching(true);
    const pollDetailedReport = async (retries = 50): Promise<any> => {
      const transcripts = transcriptRef.current || [];
      for (let i = 0; i < retries; i++) {
        if (abortRef.current) return null;
        try {
          const detailedReportWatchResponse = await api.watchDetailedReport(callDetails?.id as string);
          if (["completed", "incomplete"].includes(detailedReportWatchResponse?.data?.data)) {
            getLoggerRef()?.info('Detailed report ready', { sessionId: callDetails?.id, status: detailedReportWatchResponse?.data?.data });
            const detailedReport = await api.getDetailedReport(callDetails?.id as string);
            setDetailedReport({
              ...detailedReport?.data,
              session: {
                sessionID: callDetails?.id,
                userId: userInfo?.id || userInfo?.["sub"],
              }
            });
            setDetailedReportFetching(false);
            captureEvent(PRAXIS_PLAY_EVENTS.PRAXISPLAY_SESSION_COMPLETED, {
              session_id: callDetails?.id,
              // session_duration_ms: callDetails?.sessionDurationMs,
              feedback_received: true,
              completion_status: detailedReportWatchResponse?.data?.data === "completed" ? "success" : "partial",
              messages_exchanged: transcripts?.length || 0,
            });
            return true;
          }
        } catch (err: any) {
          captureEvent(PRAXIS_PLAY_EVENTS.PRAXISPLAY_SESSION_COMPLETED, {
            session_id: callDetails?.id,
            // session_duration_ms: callDetails?.sessionDurationMs,
            feedback_received: false,
            completion_status: "failed",
            messages_exchanged: transcripts?.length || 0,
          });
          if (err?.response?.status !== 404) {
            // Unexpected error, throw it
            throw err;
          }
          // Not ready yet, retry immediately (backend handles delay)
        }
      }
      const detailedReportTimeoutError = new Error("Max retries reached. Detailed report not available.");
      getLoggerRef()?.error(detailedReportTimeoutError.message, detailedReportTimeoutError, { sessionId: callDetails?.id, retries: 50 });
      throw detailedReportTimeoutError;
    };
    const detailedReport = await pollDetailedReport();
    if (detailedReport === null) {
      // aborted via notify — clear fetching flag so UI is not stuck waiting
      setDetailedReportFetching(false);
      return;
    }
  }

  const fetchReports = async (
    transcripts: { role: string; content: string }[],
    playbackResponse: any
  ) => {
    try {
      setFetchingReports(true);

      // Step 1: Post reports
      const response = await api.endCall(
        callDetails?.id as string,
        {
          messages: transcripts,
        }
      );
      getLoggerRef()?.info('End call posted, polling for report', { sessionId: callDetails?.id, transcriptLength: transcripts.length });
      // Step 2: Poll for detailed report
      const pollReport = async (retries = 50): Promise<any> => {
        for (let i = 0; i < retries; i++) {
          if (abortRef.current) return null;
          try {
              const watchResponse = await withBackoff(() => api.watchReport(callDetails?.id as string));
            if (["completed", "incomplete"].includes(watchResponse?.data?.data)) {
              if (watchResponse?.data?.data === "completed") {
                getLoggerRef()?.info('Report ready', { sessionId: callDetails?.id, status: watchResponse?.data?.data });
                const report = await api.fetchReport(callDetails?.id as string, {
                  messages: transcripts,
                });
                if (abortRef.current) return null;
                setReport({
                  ...report?.data?.report, // outer report (conversations, duration, and nested report object)
                  videoUrl: playbackResponse?.data?.url, 
                  session: {
                    ...report?.data?.session,
                    sessionID: callDetails?.id,
                    userId: userInfo?.id || userInfo?.["sub"],
                  }
                });
                return true;
              } else {
                return false;
              }
            }
          } catch (err: any) {
            if (err?.response?.status !== 404) {
              // Unexpected error, throw it
              throw err;
            }
            // Not ready yet, retry immediately (backend handles delay)
          }
        }
        const reportTimeoutError = new Error("Max retries reached. Detailed report not available.");
        getLoggerRef()?.error(reportTimeoutError.message, reportTimeoutError, { sessionId: callDetails?.id, retries: 50 });
        throw reportTimeoutError;
      };

      const pollReportResponse = await pollReport();
      if (abortRef.current) return null;
      if (!pollReportResponse) {
        // setDetailedReport(detailedReport?.data);
        ShowToast({
          message: "Call ended but report is not available",
          type: "error",
        });
      }

      setFetchingReports(false);
      setShowFeedback(true);
      return pollReportResponse;
    } catch (error: any) {
      if (abortRef.current) {
        setFetchingReports(false);
        return null;
      }
      getLoggerRef()?.error("PraxisPlay report fetch failed", error as Error, { sessionId: callDetails?.id });
      setFetchingReports(false);
      ShowGenericErrorToast();
    }
  };

  const onDisconnect = useCallback(async ({ user_audio, assistant_audio, endReason }: { user_audio: any, assistant_audio: any, endReason?: string }) => {
    const transcripts = transcriptRef.current || [];
    try {
      if (!callDetails?.id) {
        setCallStatus("ended");
        return;
      }
      getLoggerRef()?.info('Disconnect initiated', { sessionId: callDetails?.id, endReason, transcriptLength: transcripts.length });
      abortRef.current = false;
      setLoading(true);
      if (endReason) {
        await executeEndReasonDisconnect(
          callDetails.id as string,
          transcripts,
          endReason,
          captureEvent,
          { setLoading, setCallDetails, setCallStatus, setDataSavingProgress, setAssignment },
          transcriptRef
        );
        return;
      }
      let playbackResponse: any = null;
      if (callDetails?.isVideoRecording) {
        playbackResponse = await api.createPlayback({
          sessionId: callDetails?.id,
          userId: userInfo?.id || userInfo?.["sub"],
        });
      }
      const report = await fetchReports(transcripts, playbackResponse);
      if (abortRef.current) return;
      captureEvent(PRAXIS_PLAY_EVENTS.PRAXISPLAY_SESSION_TERMINATED, {
        session_id: callDetails?.id,
        // session_duration_ms: callDetails?.sessionDurationMs,
        termination_source: "user_action",
        messages_exchanged: transcripts?.length || 0,
      });
      transcriptRef.current = [];
      setLoading(false);
      setCallStatus("ended");
      setDataSavingProgress(0);
      await fetchDetailedReport();
    } catch (error: any) {
      if (abortRef.current) return;
      getLoggerRef()?.error("PraxisPlay call disconnect failed", error as Error, { sessionId: callDetails?.id, endReason });
      setLoading(false);
      ShowGenericErrorToast();
      if (endReason) {
        clearCallStateOnDisconnectError(
          { setLoading, setCallDetails, setCallStatus, setDataSavingProgress, setAssignment },
          transcriptRef
        );
      }
    }
  }, [callDetails]);

  const onNotifyAbort = useCallback(() => {
    abortRef.current = true;
  }, []);

  const onNotifySuccess = useCallback(() => {
    abortRef.current = true;
    transcriptRef.current = [];
    setReport({});
    setDetailedReport(null);
    setShowFeedback(false);
    setFetchingReports(false);
    setLoading(false);
    setAssignment(null);
    setCallStatus("idle");
    setCallDetails({});
  }, []);

  const values = {
    callDetails,
    setCallDetails,
    callStatus,
    report,
    setCallStatus,
    onDisconnect,
    fetchingReports,
    setLoading,
    assignment,
    setAssignment,
    transcriptRef,
    sentenceCacheRef,
    showFeedback,
    setShowFeedback,
    dataSavingProgress,
    setDataSavingProgress,
    detailedReport,
    detailedReportFetching,
  };

  return (
    <PraxisCallContext.Provider value={values}>
      {loading ? (
        <CallProcessingLoadingState
          sessionId={callDetails?.id}
          onNotifyAbort={onNotifyAbort}
          onNotifySuccess={onNotifySuccess}
        />
      ) : children}
    </PraxisCallContext.Provider>
  );
}

export function usePraxisCall(): {
  assignment: any;
  setAssignment: React.Dispatch<React.SetStateAction<any>>;
  showFeedback: boolean;
  setShowFeedback: React.Dispatch<React.SetStateAction<boolean>>;
  callDetails: {
    id?: string;
    isVideoRecording?: boolean;
    isCertificationMode?: boolean;
    clientSecret?: string;
    beginTime?: string | number;
    brandAndPI?: string;
    practitionerType?: string;
    isPracticeMode?: boolean;
    voice?: string;
    objectionLevel?: string;
    personalityType?: string;
    seedContext?: any;
  };
  setCallDetails: React.Dispatch<
    React.SetStateAction<{
      id?: string;
      isVideoRecording?: boolean;
      isCertificationMode?: boolean;
      clientSecret?: string;
      beginTime?: string | number;
      brandAndPI?: string;
      practitionerType?: string;
      isPracticeMode?: boolean;
      voice?: string;
      objectionLevel?: string;
      personalityType?: string;
      seedContext?: any;
    }>
  >;
  callStatus: string;
  setCallStatus: React.Dispatch<React.SetStateAction<string>>;
  report: unknown;
  setReport: React.Dispatch<React.SetStateAction<unknown>>;
  onDisconnect: (args: {
    user_audio: any;
    assistant_audio: any;
    endReason?: string;
  }) => Promise<void>;
  fetchingReports: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  transcriptRef: React.MutableRefObject<{ role: string; content: string }[]>;
  sentenceCacheRef: React.MutableRefObject<string[]>;
  dataSavingProgress: number;
  setDataSavingProgress: React.Dispatch<React.SetStateAction<number>>;
  detailedReport: any;
  detailedReportFetching: boolean;
} {
  const context = React.useContext(PraxisCallContext);
  if (context === undefined) {
    throw new Error("usePraxisCall must be used within a PraxisCallProvider");
  }
  return context as {
    assignment: any;
    setAssignment: React.Dispatch<React.SetStateAction<any>>;
    callDetails: {
      id?: string;
      isVideoRecording?: boolean;
      isCertificationMode?: boolean;
      beginTime?: string | number;
      isPracticeMode?: boolean;
      voice?: string;
      objectionLevel?: string;
      personalityType?: string;
      seedContext?: any;
    };
    setCallDetails: React.Dispatch<
      React.SetStateAction<{
        id?: string;
        isVideoRecording?: boolean;
        isCertificationMode?: boolean;
        beginTime?: string | number;
        isPracticeMode?: boolean;
        voice?: string;
        objectionLevel?: string;
        personalityType?: string;
        seedContext?: any;
      }>
    >;
    callStatus: string;
    fetchingReports: boolean;
    setCallStatus: React.Dispatch<React.SetStateAction<string>>;
    report: unknown;
    setReport: React.Dispatch<React.SetStateAction<unknown>>;
    onDisconnect: (args: {
      user_audio: any;
      assistant_audio: any;
      endReason?: string;
    }) => Promise<void>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    transcriptRef: React.MutableRefObject<{ role: string; content: string }[]>;
    sentenceCacheRef: React.MutableRefObject<string[]>;
    showFeedback: boolean;
    setShowFeedback: React.Dispatch<React.SetStateAction<boolean>>;
    dataSavingProgress: number;
    setDataSavingProgress: React.Dispatch<React.SetStateAction<number>>;
    detailedReport: any;
    detailedReportFetching: boolean;
  };
}
